import { DocumentMeta, Permissions, QUERY_KEYS } from "@cnx/common";
import { useListState, usePrevious } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import DocumentService from "services/DocumentService";
import { DocumentWithId, createDocumentWithId } from "types/document";

type UseRemoveDocumentsOptions = {
  contextId: string;
  onSuccess?: () => void;
};

export const useRemoveDocuments = ({
  contextId,
  onSuccess,
}: UseRemoveDocumentsOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentIds: string[]) => {
      return DocumentService.removeDocumentsByIds(documentIds);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENTS, contextId],
      });
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export type DocumentWithMeta = DocumentMeta & { file: File };

export const useDocuments = ({
  contextId,
  enabled = true,
  viewPermissions,
  onError,
}: {
  contextId?: string;
  enabled?: boolean;
  onError?: (error: unknown) => void;
  viewPermissions?: Permissions[];
}) => {
  const [isPending, setIsPending] = useState(false);
  const [documents, setDocuments] = useState<Array<DocumentWithMeta>>([]);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const { error, data, isSuccess, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.DOCUMENTS, contextId, { viewPermissions }],

    queryFn: async () => {
      if (!contextId) {
        return [];
      }

      return DocumentService.getAllDocumentsMetaByContextId({
        contextId,
        viewPermissions,
      });
    },
    enabled: Boolean(contextId) && enabled,
    retry: false,
  });

  useEffect(() => {
    if (error) onErrorRef.current?.(error);
  }, [error]);

  const prevData = usePrevious(data);
  const prevEnabled = usePrevious(enabled);

  const isFileFetchPending =
    !!data &&
    enabled &&
    (!Object.is(prevData, data) || (!prevEnabled && enabled));

  useEffect(() => {
    const fetchFiles = async () => {
      if (!data) {
        return;
      }

      if (!enabled) {
        return;
      }

      const justEnabled = !prevEnabled && enabled;

      if (Object.is(prevData, data) && !justEnabled) {
        return;
      }

      setIsPending(() => true);

      try {
        const documents: Array<DocumentWithMeta> = [];

        for (const documentMeta of data) {
          const fileExtension = documentMeta.url.split(".").pop();
          const contentType = fileExtension
            ? "application/" + fileExtension
            : undefined;

          const blob = await DocumentService.getDocumentAsBuffer(
            documentMeta.url,
            contentType
          );

          if (blob instanceof Blob) {
            const fileName = documentMeta.url.split("/").pop() || "document";
            const file = new File([blob], fileName, { type: contentType });
            documents.push({ ...documentMeta, file });
          }
        }

        setDocuments(() => documents);
      } catch (error) {
        if (onError) {
          onError(error);
        }
      } finally {
        setIsPending(() => false);
      }
    };
    fetchFiles();
  }, [data, enabled]);

  return {
    documents: documents ?? [],
    isPending: isPending || isFileFetchPending,
    isFetching,
    isSuccess,
  };
};

type DocumentManagementHookOptions = {
  onError?: () => void;
  contextId: string;
};

export const useDocumentsWithId = (initialDocuments: DocumentWithId[] = []) => {
  const [
    documents,
    { append: appendDocument, remove: removeDocument, setState: setDocuments },
  ] = useListState<DocumentWithId>(initialDocuments);

  const append = useCallback(
    (blob: Blob) => {
      const documentWithId = {
        ...createDocumentWithId(blob),
        isSaved: false,
      };
      appendDocument(documentWithId);
    },
    [appendDocument]
  );

  const removeById = useCallback(
    (id: string) => {
      const index = documents.findIndex(doc => doc.id === id);
      if (index !== -1) {
        removeDocument(index);
      }
    },
    [documents, removeDocument]
  );

  const getBlobs = useCallback((): Blob[] => {
    return documents.filter(doc => !doc.isSaved).map(doc => doc.blob);
  }, [documents]);

  const clear = useCallback(() => {
    setDocuments([]);
  }, [setDocuments]);

  return {
    documents,
    append,
    removeById,
    getBlobs,
    clear,
    setDocuments,
  };
};

export const useDocumentManagementWithId = ({
  contextId,
  onError,
}: DocumentManagementHookOptions) => {
  const { documents: savedDocuments, isPending: isDocumentsLoading } =
    useDocuments({ contextId, onError });

  const [
    documents,
    { append: appendDocument, remove: removeDocument, setState: setDocuments },
  ] = useListState<DocumentWithId>([]);
  const [documentsToRemove, { append: appendDocumentToRemove }] =
    useListState<string>([]);
  const originalViewAdminOnlyFlags = useRef<Record<string, number>>({});

  const append = useCallback(
    (blob: Blob, viewAdminOnly?: number) => {
      const documentWithId = {
        ...createDocumentWithId(blob),
        isSaved: false,
        viewAdminOnly: viewAdminOnly ?? 0,
      };
      appendDocument(documentWithId);
    },
    [appendDocument]
  );

  const changeViewAdminOnly = useCallback(
    (id: string, viewAdminOnly: number) => {
      setDocuments(currentDocuments =>
        currentDocuments.map(doc =>
          doc.id === id ? { ...doc, viewAdminOnly } : doc
        )
      );
    },
    [setDocuments]
  );

  const removeById = useCallback(
    (id: string) => {
      const index = documents.findIndex(doc => doc.id === id);
      if (index !== -1) {
        const savedDocument = savedDocuments.find(doc => doc.id === id);
        if (savedDocument) {
          appendDocumentToRemove(savedDocument.id);
        }
        removeDocument(index);
      }
    },
    [documents, savedDocuments, appendDocumentToRemove, removeDocument]
  );

  const getBlobs = useCallback((): Blob[] => {
    return documents.filter(doc => !doc.isSaved).map(doc => doc.blob);
  }, [documents]);

  const getViewAdminOnlyFlags = useCallback((): number[] => {
    return documents
      .filter(doc => !doc.isSaved)
      .map(doc => doc.viewAdminOnly ?? 0);
  }, [documents]);

  const getChangedViewAdminOnlyFlags = useCallback((): Array<{
    documentId: string;
    viewAdminOnly: number;
  }> => {
    return documents.reduce<
      Array<{ documentId: string; viewAdminOnly: number }>
    >((acc, doc) => {
      if (
        doc.isSaved &&
        originalViewAdminOnlyFlags.current[doc.id] !== doc.viewAdminOnly
      ) {
        acc.push({
          documentId: doc.id,
          viewAdminOnly: doc.viewAdminOnly ?? 0,
        });
      }
      return acc;
    }, []);
  }, [documents]);

  const clear = useCallback(() => {
    setDocuments([]);
  }, [setDocuments]);

  useEffect(() => {
    const documentsWithId = savedDocuments.map(
      ({ file, id, viewAdminOnly }) => ({
        id,
        blob: file,
        isSaved: true,
        viewAdminOnly,
      })
    );

    originalViewAdminOnlyFlags.current = savedDocuments.reduce((acc, doc) => {
      acc[doc.id] = doc.viewAdminOnly;
      return acc;
    }, {} as Record<string, number>);

    setDocuments(documentsWithId);
  }, [savedDocuments]);

  return {
    isDocumentsLoading,
    documents,
    documentsToRemove,
    append,
    changeViewAdminOnly,
    removeById,
    getBlobs,
    getViewAdminOnlyFlags,
    getChangedViewAdminOnlyFlags,
    clear,
    setDocuments,
  };
};
